import { 
  NewsArticleResponse, 
  ContentBlock,
  FlatResponse,
  BusinessResponse,
  ParkingResponse,
  FlatFeatureResponse,
  BusinessFeatureResponse,
  ParkingFeatureResponse,
  BusinessProjectFeatureResponse,
  ParkingProjectFeatureResponse,
  DocumentResponse,
  ProjectFeatureResponse,
  CareersResponse,
  ManagementResponse,
  MortgageResponse,
  PromoBlocksResponse,
  PromoResponse,
  SupportResponse,
  ContactsResponse,
} from './api.types';
import { NewsData } from '../components/NewsCard/NewsCard';
import { FlatCardData } from '../components/CardsComponents/types';

function normalizeUrlToHttps(url: string | undefined | null): string {
  if (!url) return '';
  let normalized = url.trim();
  if (!normalized) return '';
  if (normalized.startsWith('//')) {
    return `https:${normalized}`;
  }
  if (normalized.toLowerCase().startsWith('http://')) {
    normalized = 'https://' + normalized.slice('http://'.length);
  }
  if (/^(https:\/\/|data:|blob:)/i.test(normalized)) {
    return normalized;
  }
  // If protocol is missing but domain is present
  if (!/^https?:\/\//i.test(normalized) && normalized.startsWith('vostokstroy.com')) {
    normalized = `https://${normalized}`;
    return normalized;
  }
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

function normalizeOptionsList(options: unknown): Array<{ id: number; name: string; image: string | null }> {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .map((option, index) => {
      if (typeof option === 'string') {
        const name = option.trim();
        if (!name) {
          return null;
        }
        return {
          id: index + 1,
          name,
          image: null,
        };
      }

      if (option && typeof option === 'object') {
        const raw = option as { id?: number; name?: string; image?: string | null };
        const name = (raw.name ?? '').trim();
        if (!name) {
          return null;
        }

        return {
          id: typeof raw.id === 'number' ? raw.id : index + 1,
          name,
          image: raw.image ? normalizeUrlToHttps(raw.image) : null,
        };
      }

      return null;
    })
    .filter((option): option is { id: number; name: string; image: string | null } => Boolean(option));
}

function extractVkEmbedUrlFromText(text: string | undefined | null): string | null {
  if (!text) return null;

  const embedMatch = text.match(/https?:\/\/(?:www\.)?vkvideo\.ru\/video_ext\.php\?[^"'\s<>]*/i);
  if (embedMatch && embedMatch[0]) {
    return embedMatch[0];
  }

  const directMatch = text.match(/https?:\/\/(?:www\.)?(?:vk\.com|vkvideo\.ru)\/video(-)?(\d+)_([0-9]+)/i);
  if (directMatch) {
    const hasDash = !!directMatch[1];
    const rawId = directMatch[2];
    const id = directMatch[3];
    const oid = hasDash ? `-${rawId}` : rawId;
    return `https://vkvideo.ru/video_ext.php?oid=${oid}&id=${id}&hd=3&autoplay=1`;
  }

  return null;
}

function transformContentBlock(block: ContentBlock) {
  const normalizedType = (block.block_type || block.blockType) as
    | 'paragraph'
    | 'subtitle'
    | 'list'
    | 'image'
    | undefined;

  if (normalizedType === 'image') {
    const maybeVkEmbed = extractVkEmbedUrlFromText(block.text);
    if (maybeVkEmbed) {
      return {
        type: 'video',
        src: maybeVkEmbed,
        caption: block.caption || undefined,
      };
    }

    return {
      type: 'image',
      src: normalizeUrlToHttps(block.image || ''),
      caption: block.caption || undefined,
    };
  }

  const type: 'paragraph' | 'subtitle' | 'list' =
    normalizedType === 'subtitle'
      ? 'subtitle'
      : normalizedType === 'list'
      ? 'list'
      : 'paragraph';

  return {
    type,
    text: block.text || undefined,
    items: block.items && block.items.length > 0 ? block.items : undefined,
  };
}

export function transformNewsArticle(article: NewsArticleResponse): NewsData {
  return {
    id: article.id,
    project: article.project,
    topic: article.topic,
    date: article.date,
    title: article.title,
    shortDescription: article.shortDescription,
    content: article.content ? article.content.map(transformContentBlock) : undefined,
  };
}

export function transformNewsArticles(articles: NewsArticleResponse[]): NewsData[] {
  return articles.map(transformNewsArticle);
}

export function transformFlat(flat: any): FlatCardData {
  return {
    id: flat.id,
    available: flat.available ?? false,
    popular: flat.popular ?? false,
    area: flat.area ?? 0,
    livingArea: flat.livingArea ?? 0,
    roomType: flat.roomType || '',
    floor: flat.floor ?? 0,
    totalFloors: flat.totalFloors ?? 0,
    entrance: flat.entrance ?? 0,
    totalEntrances: flat.totalEntrances ?? 0,
    pricePerM2: flat.pricePerM2 !== undefined && flat.pricePerM2 !== null ? String(flat.pricePerM2) : '0',
    price: flat.price !== undefined && flat.price !== null ? String(flat.price) : '0',
    windowOptions: normalizeOptionsList(flat.windowOptions ?? flat.window_options),
    specialOptions: normalizeOptionsList(flat.specialOptions ?? flat.special_options),
    image: normalizeUrlToHttps(flat.image) || '',
    imageSecond: normalizeUrlToHttps(flat.imageSecond) || '',
    previewPlan: normalizeUrlToHttps(flat.previewPlan) || '',
    previewVisual: normalizeUrlToHttps(flat.previewVisual) || '',
    previewFloor: normalizeUrlToHttps(flat.previewFloor) || '',
    preview3DTour: normalizeUrlToHttps(flat.preview3dTour) || '',
    discountProperty: flat.discountProperty ?? 0,
    discountFull: typeof flat.discountFull === 'string' ? parseFloat(flat.discountFull) : (flat.discountFull ?? 0),
    type: flat.type || '',
    projectType: flat.projectType || '',
    checkIn: flat.checkIn || '',
    cellingHeight: flat.ceillingHeight ?? 0,
  };
}

export function transformFlats(flats: FlatResponse[]): FlatCardData[] {
  return flats.map(transformFlat);
}

export function transformFlatFeatures(features: FlatFeatureResponse[]) {
  return features.map(feature => ({
    id: feature.id,
    text: feature.text,
    image: feature.image,
  }));
}

export function transformBusiness(business: BusinessResponse) {
  return {
    id: business.id,
    available: business.available,
    popular: business.popular,
    type: business.type,
    area: business.area !== undefined && business.area !== null
      ? business.area.toString()
      : '',
    numberPlace: business.numberPlace,
    floor: business.floor,
    totalFloors: business.totalFloors,
    pricePerM2: business.pricePerM2 !== undefined && business.pricePerM2 !== null
      ? business.pricePerM2.toString()
      : '',
    price: business.price !== undefined && business.price !== null
      ? business.price.toString()
      : '',
    image: normalizeUrlToHttps(business.image),
    imageSecond: '',
    corpus: '',
    showcaseWindows: false,
    windowOptions: normalizeOptionsList((business as any).windowOptions ?? (business as any).window_options),
    specialOptions: normalizeOptionsList((business as any).specialOptions ?? (business as any).special_options),
    projectType: business.projectType,
    cellingHeight: business.cellingHeight,
    checkIn: business.checkIn,
    totalNumberPlaces: business.totalNumberPlaces,
    previewPlan: normalizeUrlToHttps(business.previewPlan),
    previewFloor: normalizeUrlToHttps((business as any).previewFloor),
    previewVisual: normalizeUrlToHttps((business as any).previewVisual),
    discountProperty: business.discountProperty,
    discountFull: business.discountFull,
  };
}

export function transformBusinesses(businesses: BusinessResponse[]) {
  return businesses.map(transformBusiness);
}

export function transformBusinessFeatures(features: BusinessFeatureResponse[]) {
  return features.map(feature => ({
    id: feature.id,
    text: feature.text,
    image: feature.image,
  }));
}

export function transformBusinessProjectFeatures(projects: BusinessProjectFeatureResponse[]) {
  return projects.map(project => ({
    projectId: project.project.projectId,
    title: project.description || 'Коммерческие помещения',
    description: project.description || '',
    preview: project.preview || '',
    image: project.image || '',
    features: project.features.map(feature => ({
      id: feature.id,
      text: feature.text,
      image: feature.image,
    })),
    businessCount: 0,
    minPrice: 0,
  }));
}


export function transformParking(parking: ParkingResponse) {
  return {
    id: (parking as any).numberPlace,
    area: parking.area !== undefined && parking.area !== null
      ? parking.area.toString()
      : '',
    numberPlace: (parking as any).numberPlace,
    floor: parking.floor,
    totalFloors: parking.total_floors,
    parkingType: parking.parking_type,
    pricePerM2: parking.price_per_m2 !== undefined && parking.price_per_m2 !== null
      ? parking.price_per_m2.toString()
      : '',
    price: parking.price !== undefined && parking.price !== null
      ? parking.price.toString()
      : '',
    available: parking.available,
    popular: parking.popular,
    type: parking.type,
    totalNumberPlaces: parking.total_number_places,
    checkIn: parking.check_in,
    previewPlan: normalizeUrlToHttps(parking.preview_plan),
    discountProperty: parking.discount_property,
    discountFull: parking.discount_full,
  };
}

export function transformParkings(parkings: ParkingResponse[]) {
  return parkings.map(transformParking);
}

export function transformParkingFeatures(features: ParkingFeatureResponse[]) {
  return features.map(feature => ({
    id: feature.id,
    text: feature.text,
    image: feature.image,
  }));
}

export function transformParkingProjectFeatures(projects: ParkingProjectFeatureResponse[]) {
  return projects.map(project => ({
    project: project.project,
    title: project.description || 'Парковочные места',
    description: project.description || '',
    preview: project.preview || '',
    image: project.image || '',
    features: project.features.map(feature => ({
      id: feature.id,
      text: feature.text,
      image: feature.image,
    })),
    parkingCount: 0,
    minPrice: 0,
  }));
}

export function transformDocument(document: DocumentResponse) {
  return {
    date: document.date,
    title: document.title,
    type: document.type,
    file: document.file,
  };
}

export function transformDocuments(documents: DocumentResponse[]) {
  return documents.map(transformDocument);
}

export function transformProjectFeatures(projects: ProjectFeatureResponse[]) {
  return projects.map(project => ({
    projectId: project.project.projectId,
    title: project.description || 'Жилой комплекс',
    image: project.image || '',
    preview: project.preview || '',
    description: project.description || '',
    features: project.features.map(feature => ({
      id: feature.id,
      text: feature.text,
      image: feature.image,
    })),
    buttons: project.buttons.map(button => ({ text: button.text })),
    minPrice: 0,
  }));
}

export function transformCareers(careers: CareersResponse) {
  let tasksArray: string[] = [];
  if (typeof careers.tasks === 'string') {
    try {
      const parsed = JSON.parse(careers.tasks);
      tasksArray = Array.isArray(parsed) ? parsed : [careers.tasks];
    } catch (e) {
      tasksArray = careers.tasks.split('\r\n').filter(task => task.trim());
    }
  } else if (Array.isArray(careers.tasks)) {
    tasksArray = careers.tasks;
  }

  let requirementsArray: string[] = [];
  if (typeof careers.requirements === 'string') {
    try {
      const parsed = JSON.parse(careers.requirements);
      requirementsArray = Array.isArray(parsed) ? parsed : [careers.requirements];
    } catch (e) {
      requirementsArray = careers.requirements.split('\r\n').filter(req => req.trim());
    }
  } else if (Array.isArray(careers.requirements)) {
    requirementsArray = careers.requirements;
  }

  return {
    id: careers.id,
    title: careers.title,
    location: careers.location,
    work_hours: careers.workHours,
    tasks: tasksArray,
    requirements: requirementsArray,
  };
}

export function transformCareersList(careers: CareersResponse[]) {
  return careers.map(transformCareers);
}

export function transformManagement(management: ManagementResponse) {
  let projectsArray: string[] = [];
  
  if (typeof management.projects === 'string') {
    try {
      const parsed = JSON.parse(management.projects);
      projectsArray = Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      projectsArray = [];
    }
  } else if (Array.isArray(management.projects)) {
    projectsArray = management.projects.map(project => project.project_id);
  }

  return {
    id: management.id,
    name: management.name,
    phone: management.phone1,
    projects: projectsArray,
    dispatcher_phone: management.phone2 || management.phone1,
    email: management.email || '',
    working_hours: management.workHours,
  };
}

export function transformManagementList(managementList: ManagementResponse[]) {
  return managementList.map(transformManagement);
}

export function transformMortgage(mortgage: MortgageResponse) {
  return {
    id: mortgage.id,
    period: mortgage.period,
  };
}

export function transformMortgages(mortgages: MortgageResponse[]) {
  return mortgages.map(transformMortgage);
}

export function transformPromoBlock(promoBlock: PromoBlocksResponse) {
  return {
    id: promoBlock.id,
    type: promoBlock.type as 'big' | 'small',
    title: promoBlock.title,
    text: promoBlock.text,
    link: promoBlock.link,
    image: promoBlock.image,
    bgColor: promoBlock.bgColor,
  };
}

export function transformPromoBlocks(promoBlocks: PromoBlocksResponse[]) {
  return promoBlocks.map(transformPromoBlock);
}

export function transformPromo(promo: PromoResponse) {
  return {
    id: promo.id,
    title: promo.title,
    text: promo.description,
    promo: promo.image,
    bgColor: promo.bgColor
  };
}

export function transformPromos(promos: PromoResponse[]) {
  return promos.map(transformPromo);
}

export function transformSupport(support: SupportResponse) {
  return {
    id: support.id,
    tabTitle: support.tabTitle,
    questions: support.questions.map(question => ({
      id: question.id,
      question: question.question,
      answer: question.answer,
    })),
  };
}

export function transformSupportList(supportList: SupportResponse[]) {
  return supportList.map(transformSupport);
} 

export function transformContacts(contacts: ContactsResponse) {
  return {
    id: contacts.id,
    type: contacts.type,
    title: contacts.title,
    location: contacts.location,
    workHours: contacts.workHours,
    phone: contacts.phone,
    email: contacts.email,
    mapLink: contacts.mapLink,
    image: contacts.image,
  };
}

export function transformContactsList(contactsList: ContactsResponse[]) {
  return contactsList.map(transformContacts);
}
